import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@apollo/client/react";
import keyBy from "lodash/keyBy";
import isEmpty from "lodash/isEmpty";
import { useLocation, useParams } from "react-router";

import {
  SHOPPING_INVENTORY_CARTS_QUERY,
  SHOPPING_INVENTORY_ITEMS_QUANTIY,
} from "../constants/graphql/index";
import { usePermissions } from "./permissions";

const ShoppingContext = React.createContext(null);

const cartIdFromSearch = search => {
  const params = new URLSearchParams(search);
  return params.get("cartId");
};

export const ShoppingProvider = ({
  children,
  inventoryRequestQuery = SHOPPING_INVENTORY_CARTS_QUERY,
  fetchCartQuantity = false,
}) => {
  const queryToUse = fetchCartQuantity ? SHOPPING_INVENTORY_ITEMS_QUANTIY : inventoryRequestQuery;
  const { user } = usePermissions();
  const location = useLocation();
  const { projectId } = useParams();
  const [inventoryRequests, setInventoryRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCartId, setSelectedCartId] = useState(() => cartIdFromSearch(location.search));

  const projectPartitionedShoppingCart =
    !!user?.company?.settings?.project_partitioned_shopping_cart;

  useEffect(() => {
    const nextCartId = cartIdFromSearch(location.search);
    setSelectedCartId(previousCartId => (previousCartId === nextCartId ? previousCartId : nextCartId));
  }, [location.search]);

  const activeInventoryRequestId = useMemo(() => {
    if (!inventoryRequests.length) return null;
    if (!projectPartitionedShoppingCart) return inventoryRequests[0]?.id || null;

    if (selectedCartId && inventoryRequests.some(cart => `${cart.id}` === selectedCartId)) {
      return selectedCartId;
    }

    if (projectId) {
      const matchingProjectCart = inventoryRequests.find(
        cart =>
          cart.shoppingCartKind === "project" &&
          `${cart.shoppingCartProjectId}` === `${projectId}`
      );
      if (matchingProjectCart) return matchingProjectCart.id;
    }

    return inventoryRequests[0]?.id || null;
  }, [inventoryRequests, projectId, projectPartitionedShoppingCart, selectedCartId]);

  const inventoryRequest = useMemo(
    () => inventoryRequests.find(cart => `${cart.id}` === `${activeInventoryRequestId}`) || null,
    [activeInventoryRequestId, inventoryRequests]
  );

  const aggregatedInventoryItems = useMemo(
    () => inventoryRequests.flatMap(cart => cart.inventoryItems || []),
    [inventoryRequests]
  );

  const inventoryItems = useMemo(
    () => keyBy(fetchCartQuantity ? aggregatedInventoryItems : inventoryRequest?.inventoryItems || [], "id"),
    [aggregatedInventoryItems, fetchCartQuantity, inventoryRequest]
  );
  const inventoryAssets = useMemo(
    () => keyBy(inventoryRequest?.assets || [], "id"),
    [inventoryRequest]
  );
  const totalCartItemCount = useMemo(
    () =>
      inventoryRequests.reduce(
        (sum, cart) => sum + (Array.isArray(cart.inventoryItems) ? cart.inventoryItems.length : 0),
        0
      ),
    [inventoryRequests]
  );

  const normalizeInventoryRequests = useCallback(data => {
    if (Array.isArray(data?.shoppingInventoryCarts)) return data.shoppingInventoryCarts;
    if (Array.isArray(data?.shoppingInventoryRequest)) return data.shoppingInventoryRequest;
    if (data?.shoppingInventoryRequest) return [data.shoppingInventoryRequest];
    return [];
  }, []);

  const updateInventoryRequests = useCallback(updater => {
    setInventoryRequests(previousRequests => updater(previousRequests));
  }, []);

  const updateInventoryAsset = (assetId, props) => {
    updateInventoryRequests(previousRequests =>
      previousRequests.map(cart => ({
        ...cart,
        assets: (cart.assets || []).map(asset =>
          asset.id === assetId ? { ...asset, ...props } : asset
        ),
        inventoryItems: (cart.inventoryItems || []).map(item =>
          item.assetId === assetId && item.asset ? { ...item, asset: { ...item.asset, ...props } } : item
        ),
      }))
    );
  };

  const setInventoryItem = inventoryItem => {
    updateInventoryRequests(previousRequests =>
      previousRequests.map(cart => {
        if (`${cart.id}` !== `${inventoryItem.inventoryRequestId}`) return cart;

        const existingItems = cart.inventoryItems || [];
        const nextItems = existingItems.some(item => item.id === inventoryItem.id)
          ? existingItems.map(item => (item.id === inventoryItem.id ? inventoryItem : item))
          : [...existingItems, inventoryItem];

        return { ...cart, inventoryItems: nextItems };
      })
    );
  };

  const setInventoryAsset = useCallback(
    asset => {
      updateInventoryRequests(previousRequests =>
        previousRequests.map(cart => ({
          ...cart,
          assets: (cart.assets || []).map(existingAsset =>
            existingAsset.id === asset.id ? asset : existingAsset
          ),
        }))
      );
    },
    [updateInventoryRequests]
  );

  const removeInventoryItem = itemId => {
    if (!itemId) return;
    updateInventoryRequests(previousRequests =>
      previousRequests
        .map(cart => ({
          ...cart,
          inventoryItems: (cart.inventoryItems || []).filter(item => item.id !== itemId),
        }))
        .filter(cart => {
          if (!projectPartitionedShoppingCart) return true;
          if ((cart.inventoryItems || []).length > 0) return true;
          return cart.shoppingCartKind === "legacy";
        })
    );
  };

  const { error, refetch, data: queryData } = useQuery(queryToUse, {
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    if (queryData) {
      setInventoryRequests(normalizeInventoryRequests(queryData));
      setLoading(false);
    }
  }, [normalizeInventoryRequests, queryData]);

  const reFetch = () => {
    refetch().then(({ data }) => {
      setInventoryRequests(normalizeInventoryRequests(data));
      setLoading(false);
    });
  };

  const value = useMemo(
    () => ({
      inventoryRequest,
      inventoryRequests,
      inventoryAssets,
      inventoryItems,
      loading,
      setInventoryRequest: inventoryRequestValue =>
        updateInventoryRequests(previousRequests =>
          previousRequests.map(cart =>
            cart.id === inventoryRequestValue?.id ? inventoryRequestValue : cart
          )
        ),
      setActiveInventoryRequestId: setSelectedCartId,
      activeInventoryRequestId,
      totalCartItemCount,
      projectPartitionedShoppingCart,
      setInventoryItem,
      setInventoryAsset,
      updateInventoryAsset,
      removeInventoryItem,
      cartIsEmpty: isEmpty(inventoryItems),
      error,
      refetch: reFetch,
    }),
    [
      activeInventoryRequestId,
      error,
      inventoryAssets,
      inventoryItems,
      inventoryRequest,
      inventoryRequests,
      loading,
      projectPartitionedShoppingCart,
      reFetch,
      selectedCartId,
      setInventoryAsset,
      totalCartItemCount,
      updateInventoryRequests,
    ]
  );

  return <ShoppingContext.Provider value={value}>{children}</ShoppingContext.Provider>;
};

export const useShopping = () => React.useContext(ShoppingContext);
